import "./style.css";

import { marked } from "marked";
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { STLLoader } from "three/addons/loaders/STLLoader.js";

const elements = {
  projectList: document.querySelector("#project-list"),
  buildRef: document.querySelector("#build-ref"),
  projectId: document.querySelector("#project-id"),
  projectTitle: document.querySelector("#project-title"),
  projectDescription: document.querySelector("#project-description"),
  fileCount: document.querySelector("#file-count"),
  themeToggle: document.querySelector("#theme-toggle"),
  themeLabel: document.querySelector("#theme-label"),
  artifactList: document.querySelector("#artifact-list"),
  viewerCard: document.querySelector("#viewer-card"),
  viewer: document.querySelector("#viewer"),
  canvas: document.querySelector("#cad-canvas"),
  drawing: document.querySelector("#drawing-preview"),
  pdf: document.querySelector("#pdf-preview"),
  markdown: document.querySelector("#markdown-preview"),
  text: document.querySelector("#text-preview"),
  message: document.querySelector("#viewer-message"),
  viewerFile: document.querySelector("#viewer-file"),
  interactionHint: document.querySelector(".interaction-hint"),
  download: document.querySelector("#download-button"),
  sourceToggle: document.querySelector("#source-toggle-button"),
  assemblyControls: document.querySelector("#assembly-controls"),
  visibilityControls: document.querySelector("#visibility-controls"),
  serviceToolControls: document.querySelector("#service-tool-controls"),
  serviceToolLabel: document.querySelector("#service-tool-label"),
  serviceToolVisible: document.querySelector("#service-tool-visible"),
  serviceInterfaceControls: document.querySelector("#service-interface-controls"),
  measure: document.querySelector("#measure-button"),
  clearMeasurement: document.querySelector("#clear-measurement-button"),
  measurementStatus: document.querySelector("#measurement-status"),
  wireframe: document.querySelector("#wireframe-button"),
  fit: document.querySelector("#fit-button"),
  fullscreen: document.querySelector("#fullscreen-button"),
  selectedDependency: document.querySelector("#selected-dependency"),
  requiresTree: document.querySelector("#requires-tree"),
  requiredByTree: document.querySelector("#required-by-tree"),
  bomPanel: document.querySelector("#bom-panel"),
  bomLineCount: document.querySelector("#bom-line-count"),
  bomRows: document.querySelector("#bom-rows"),
  bomTree: document.querySelector("#bom-tree"),
};

function setTheme(theme, persist = true) {
  const selected = theme === "dark" ? "dark" : "light";
  document.documentElement.dataset.theme = selected;
  elements.themeLabel.textContent = selected === "dark" ? "Light mode" : "Dark mode";
  elements.themeToggle.setAttribute("aria-pressed", String(selected === "dark"));
  elements.themeToggle.setAttribute(
    "aria-label",
    `Switch to ${selected === "dark" ? "light" : "dark"} mode`,
  );
  if (persist) localStorage.setItem("scripted-cad-theme", selected);
}

setTheme(document.documentElement.dataset.theme, false);
elements.themeToggle.addEventListener("click", () => {
  setTheme(document.documentElement.dataset.theme === "dark" ? "light" : "dark");
});

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 10000);
const renderer = new THREE.WebGLRenderer({
  canvas: elements.canvas,
  antialias: true,
  alpha: false,
});
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.setClearColor(0xd6dbde, 1);

const controls = new OrbitControls(camera, elements.canvas);
controls.enableRotate = true;
controls.enableZoom = true;
controls.enablePan = true;
controls.enableDamping = true;
controls.dampingFactor = 0.1;
controls.screenSpacePanning = true;
controls.zoomToCursor = true;
controls.mouseButtons.LEFT = THREE.MOUSE.ROTATE;
controls.mouseButtons.MIDDLE = THREE.MOUSE.DOLLY;
controls.mouseButtons.RIGHT = THREE.MOUSE.PAN;
controls.touches.ONE = THREE.TOUCH.ROTATE;
controls.touches.TWO = THREE.TOUCH.DOLLY_PAN;

scene.add(new THREE.AmbientLight(0xffffff, 0.55));
scene.add(new THREE.HemisphereLight(0xffffff, 0x89959c, 0.75));
const keyLight = new THREE.DirectionalLight(0xffffff, 1.35);
keyLight.position.set(3, 5, 4);
scene.add(keyLight);
const fillLight = new THREE.DirectionalLight(0xdce7ee, 0.65);
fillLight.position.set(-4, 2, -3);
scene.add(fillLight);
const grid = new THREE.GridHelper(600, 30, 0x7d898f, 0xadb6ba);
grid.material.opacity = 0.42;
grid.material.transparent = true;
scene.add(grid);
const measurementLayer = new THREE.Group();
measurementLayer.name = "measurements";
scene.add(measurementLayer);
const measurementRaycaster = new THREE.Raycaster();
const measurementPointer = new THREE.Vector2();

let catalog;
let activeProject;
let activePath;
let activeModel;
let activeAssemblyConfig;
let activeTool;
let activeToolInterface;
let modelExtent = 100;
let edgesVisible = true;
let markdownSourceVisible = false;
let measurementMode = false;
let measurementPointerStart;
let measurementPoints = [];
const semanticGroups = new Map();
const modelCache = new Map();

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = units[0];
  for (let index = 1; value >= 1024 && index < units.length; index += 1) {
    value /= 1024;
    unit = units[index];
  }
  return `${value.toFixed(value >= 10 ? 1 : 2)} ${unit}`;
}

function shortPath(path) {
  const parts = path.split("/");
  return parts.length > 2 ? `${parts[0]}/…/${parts.at(-1)}` : path;
}

function setPreviewMode(mode, message = "") {
  if (mode !== "model") {
    setMeasurementMode(false);
    clearMeasurement();
  }
  elements.canvas.hidden = mode !== "model";
  elements.drawing.hidden = mode !== "drawing";
  elements.pdf.hidden = mode !== "pdf";
  elements.markdown.hidden = mode !== "markdown";
  elements.text.hidden = mode !== "text";
  elements.message.hidden = !message;
  elements.message.textContent = message;
  elements.wireframe.disabled = mode !== "model";
  elements.fit.disabled = mode !== "model";
  elements.measure.disabled = mode !== "model" || !activeModel;
  elements.sourceToggle.hidden = mode !== "markdown";
  elements.assemblyControls.hidden = mode !== "model" || !activeAssemblyConfig;
  elements.interactionHint.textContent =
    mode === "model"
      ? measurementMode
        ? "Click two model surfaces · Esc to stop measuring"
        : "Drag to rotate · Scroll to zoom · Right-drag to pan"
      : mode === "pdf"
        ? "Use the PDF controls to zoom or print"
        : mode === "markdown"
          ? "Rendered project documentation"
          : "File preview";
}

function disposeMeasurementObjects() {
  for (const object of [...measurementLayer.children]) {
    measurementLayer.remove(object);
    object.geometry?.dispose();
    if (Array.isArray(object.material)) {
      object.material.forEach((material) => material.dispose());
    } else {
      object.material?.dispose();
    }
  }
}

function measurementDistance() {
  if (measurementPoints.length !== 2) return undefined;
  return measurementPoints[0].point.distanceTo(measurementPoints[1].point);
}

function measurementNormalDistance() {
  if (measurementPoints.length !== 2) return undefined;
  const [first, second] = measurementPoints;
  if (!first.normal) return undefined;

  let axis = first.normal.clone();
  if (second.normal && Math.abs(first.normal.dot(second.normal)) > 0.95) {
    const alignedSecond = second.normal.clone();
    if (axis.dot(alignedSecond) < 0) alignedSecond.negate();
    axis.add(alignedSecond).normalize();
  }
  return Math.abs(second.point.clone().sub(first.point).dot(axis));
}

function updateMeasurementStatus(message) {
  const distance = measurementDistance();
  const normalDistance = measurementNormalDistance();
  let status = message;
  if (!status && distance !== undefined) {
    status = `Total: ${distance.toFixed(2)} mm`;
    if (normalDistance !== undefined) {
      status += `\nNormal projection: ${normalDistance.toFixed(2)} mm`;
    }
  }
  if (!status && measurementMode) {
    status = measurementPoints.length ? "Pick second point" : "Pick first point";
  }
  elements.measurementStatus.hidden = !status;
  elements.measurementStatus.textContent = status || "";
  elements.clearMeasurement.hidden = measurementPoints.length === 0;
}

function clearMeasurement() {
  measurementPoints = [];
  disposeMeasurementObjects();
  updateMeasurementStatus();
}

function setMeasurementMode(enabled) {
  measurementMode = Boolean(enabled && activeModel);
  controls.enabled = !measurementMode;
  elements.measure.classList.toggle("active", measurementMode);
  elements.measure.setAttribute("aria-pressed", String(measurementMode));
  elements.canvas.classList.toggle("measuring", measurementMode);
  elements.interactionHint.textContent = measurementMode
    ? "Click two model surfaces · Esc to stop measuring"
    : "Drag to rotate · Scroll to zoom · Right-drag to pan";
  updateMeasurementStatus();
}

function addMeasurementMarker(point) {
  const marker = new THREE.Mesh(
    new THREE.SphereGeometry(Math.max(modelExtent * 0.009, 0.4), 18, 12),
    new THREE.MeshBasicMaterial({ color: 0xcf222e, depthTest: false }),
  );
  marker.position.copy(point);
  marker.renderOrder = 20;
  measurementLayer.add(marker);
}

function renderMeasurement() {
  disposeMeasurementObjects();
  measurementPoints.forEach(({ point }) => addMeasurementMarker(point));
  if (measurementPoints.length === 2) {
    const geometry = new THREE.BufferGeometry().setFromPoints(
      measurementPoints.map(({ point }) => point),
    );
    const material = new THREE.LineBasicMaterial({
      color: 0xcf222e,
      depthTest: false,
    });
    const line = new THREE.Line(geometry, material);
    line.renderOrder = 19;
    measurementLayer.add(line);
  }
  updateMeasurementStatus();
}

function pickMeasurementPoint(event) {
  if (!measurementMode || !activeModel) return;
  const bounds = elements.canvas.getBoundingClientRect();
  measurementPointer.set(
    ((event.clientX - bounds.left) / bounds.width) * 2 - 1,
    -((event.clientY - bounds.top) / bounds.height) * 2 + 1,
  );
  measurementRaycaster.setFromCamera(measurementPointer, camera);
  const hit = measurementRaycaster
    .intersectObject(activeModel, true)
    .find(
      (intersection) =>
        intersection.object.isMesh && isVisibleInActiveModel(intersection.object),
    );
  if (!hit) {
    updateMeasurementStatus("Click a model surface");
    return;
  }
  if (measurementPoints.length === 2) measurementPoints = [];
  const normal = hit.face?.normal.clone();
  normal?.transformDirection(hit.object.matrixWorld).normalize();
  measurementPoints.push({ point: hit.point.clone(), normal });
  renderMeasurement();
}

function showMarkdownSource(showSource) {
  markdownSourceVisible = showSource;
  elements.markdown.hidden = showSource;
  elements.text.hidden = !showSource;
  elements.sourceToggle.textContent = showSource ? "View rendered" : "View source";
  elements.interactionHint.textContent = showSource
    ? "Markdown source"
    : "Rendered project documentation";
}

function disposeModel() {
  setMeasurementMode(false);
  clearMeasurement();
  if (activeModel) {
    scene.remove(activeModel);
    disposeObjectResources(activeModel);
  }
  activeModel = undefined;
  activeAssemblyConfig = undefined;
  activeTool = undefined;
  activeToolInterface = undefined;
  semanticGroups.clear();
  elements.assemblyControls.hidden = true;
  elements.visibilityControls.replaceChildren();
  elements.serviceInterfaceControls.replaceChildren();
}

function disposeObjectResources(root) {
  root.traverse((object) => {
    object.geometry?.dispose();
    if (Array.isArray(object.material)) {
      object.material.forEach((material) => material.dispose());
    } else {
      object.material?.dispose();
    }
  });
}

function meshColor(values, fallback = 0xb9c2c8) {
  if (!values || values.length < 3) return new THREE.Color(fallback);
  const scale = Math.max(...values) > 1 ? 255 : 1;
  return new THREE.Color(
    values[0] / scale,
    values[1] / scale,
    values[2] / scale,
  );
}

function stepMaterial(color) {
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.72,
    metalness: 0.02,
    side: THREE.DoubleSide,
  });
}

function stepMeshMaterials(
  geometry,
  imported,
  colorSource,
  fallbackColor,
) {
  const source = colorSource || imported;
  const defaultValues = source.color || imported.color;
  const sourceFaces = source.brep_faces || [];
  const targetFaces = imported.brep_faces || [];
  const hasFaceColors = sourceFaces.some((face) => face.color);
  const materials = [];
  const materialIndexes = new Map();

  function materialIndex(values) {
    const color = meshColor(values, fallbackColor);
    const key = color.getHexString();
    if (!materialIndexes.has(key)) {
      materialIndexes.set(key, materials.length);
      materials.push(stepMaterial(color));
    }
    return materialIndexes.get(key);
  }

  const defaultMaterial = materialIndex(defaultValues);
  if (!hasFaceColors || !targetFaces.length) return materials[defaultMaterial];

  geometry.clearGroups();
  const triangleCount = imported.index.array.length / 3;
  let cursor = 0;

  function addTriangleGroup(first, last, index) {
    if (last < first) return;
    const start = first * 3;
    const count = (last - first + 1) * 3;
    const previousGroup = geometry.groups.at(-1);
    if (
      previousGroup &&
      previousGroup.materialIndex === index &&
      previousGroup.start + previousGroup.count === start
    ) {
      previousGroup.count += count;
      return;
    }
    geometry.addGroup(start, count, index);
  }

  for (const [faceIndex, face] of targetFaces.entries()) {
    const first = Math.max(0, Math.min(face.first, triangleCount - 1));
    const last = Math.max(first, Math.min(face.last, triangleCount - 1));
    if (first > cursor) addTriangleGroup(cursor, first - 1, defaultMaterial);
    const faceValues = sourceFaces[faceIndex]?.color || defaultValues;
    addTriangleGroup(first, last, materialIndex(faceValues));
    cursor = Math.max(cursor, last + 1);
  }
  if (cursor < triangleCount) {
    addTriangleGroup(cursor, triangleCount - 1, defaultMaterial);
  }
  return materials.length === 1 ? materials[0] : materials;
}

function resultToObject(
  result,
  { fallbackColor = 0xb9c2c8, colorResult } = {},
) {
  const group = new THREE.Group();
  for (const [meshIndex, imported] of result.meshes.entries()) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(imported.attributes.position.array, 3),
    );
    if (imported.attributes.normal) {
      geometry.setAttribute(
        "normal",
        new THREE.Float32BufferAttribute(imported.attributes.normal.array, 3),
      );
    } else {
      geometry.computeVertexNormals();
    }
    geometry.setIndex(imported.index.array);
    const material = stepMeshMaterials(
      geometry,
      imported,
      colorResult?.meshes?.[meshIndex],
      fallbackColor,
    );
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = imported.name || "STEP body";
    const edgeGeometry = new THREE.EdgesGeometry(geometry, 24);
    const edges = new THREE.LineSegments(
      edgeGeometry,
      new THREE.LineBasicMaterial({
        color: 0x34434c,
        transparent: true,
        opacity: 0.78,
      }),
    );
    edges.name = "model-edges";
    mesh.add(edges);
    group.add(mesh);
  }
  return group;
}

function stlToObject(geometry) {
  if (!geometry.getAttribute("normal")) geometry.computeVertexNormals();
  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshStandardMaterial({
      color: 0xb9c2c8,
      roughness: 0.72,
      metalness: 0.02,
      side: THREE.DoubleSide,
    }),
  );
  mesh.name = "STL mesh";
  const edges = new THREE.LineSegments(
    new THREE.EdgesGeometry(geometry, 24),
    new THREE.LineBasicMaterial({
      color: 0x34434c,
      transparent: true,
      opacity: 0.78,
    }),
  );
  edges.name = "model-edges";
  mesh.add(edges);
  const group = new THREE.Group();
  group.add(mesh);
  return group;
}

function normalizeModel(group) {
  group.rotation.x = -Math.PI / 2;
  group.updateMatrixWorld(true);
  const bounds = new THREE.Box3().setFromObject(group);
  const center = bounds.getCenter(new THREE.Vector3());
  group.position.sub(center);
  group.updateMatrixWorld(true);
  return group;
}

function isVisibleInActiveModel(object) {
  let current = object;
  while (current) {
    if (!current.visible) return false;
    if (current === activeModel) return true;
    current = current.parent;
  }
  return false;
}

function visibleModelBounds() {
  const bounds = new THREE.Box3();
  activeModel?.updateMatrixWorld(true);
  activeModel?.traverse((object) => {
    if (!object.isMesh || !isVisibleInActiveModel(object)) return;
    object.geometry.computeBoundingBox();
    const objectBounds = object.geometry.boundingBox.clone();
    objectBounds.applyMatrix4(object.matrixWorld);
    bounds.union(objectBounds);
  });
  return bounds;
}

function fitCamera() {
  if (!activeModel) return;
  const bounds = visibleModelBounds();
  if (bounds.isEmpty()) return;
  const size = bounds.getSize(new THREE.Vector3());
  modelExtent = Math.max(size.x, size.y, size.z, 1);
  camera.near = Math.max(modelExtent / 1000, 0.01);
  camera.far = modelExtent * 30;
  camera.position.set(modelExtent * 1.15, modelExtent * 0.82, modelExtent * 1.35);
  camera.updateProjectionMatrix();
  controls.minDistance = modelExtent * 0.08;
  controls.maxDistance = modelExtent * 12;
  controls.target.set(0, 0, 0);
  controls.update();
}

async function readStepArtifact(artifact) {
  let result = modelCache.get(artifact.url);
  if (result) return result;

  if (!artifact.mesh) {
    throw new Error(`No precomputed mesh for ${artifact.path}`);
  }
  const response = await fetch(`./generated/${artifact.mesh}`);
  if (!response.ok) throw new Error(`HTTP ${response.status} for ${artifact.path}`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  // Guard against transparent decompression by intermediaries.
  const gzipped = bytes[0] === 0x1f && bytes[1] === 0x8b;
  const text = gzipped
    ? await new Response(
        new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip")),
      ).text()
    : new TextDecoder().decode(bytes);
  result = JSON.parse(text);
  modelCache.set(artifact.url, result);
  return result;
}

async function readStepArtifactWithColors(artifact) {
  const result = await readStepArtifact(artifact);
  if (!artifact.color_source) return [result, undefined];
  const colorArtifact = artifactFor(artifact.color_source);
  if (!colorArtifact) {
    throw new Error(`Unknown STEP color source ${artifact.color_source}`);
  }
  return [result, await readStepArtifact(colorArtifact)];
}

function toggleEdges(root, visible) {
  root?.traverse((object) => {
    if (object.name === "model-edges") object.visible = visible;
  });
}

async function showServiceTool() {
  const tool = activeAssemblyConfig?.tool;
  if (!tool || !activeModel) return;
  const interfaceConfig = tool.interfaces.find(
    (item) => item.id === activeToolInterface,
  );
  if (!elements.serviceToolVisible.checked || !interfaceConfig) {
    if (activeTool) activeTool.visible = false;
    clearMeasurement();
    return;
  }

  if (activeTool?.userData.interfaceId === interfaceConfig.id) {
    activeTool.visible = true;
    clearMeasurement();
    return;
  }

  if (activeTool) {
    activeModel.remove(activeTool);
    disposeObjectResources(activeTool);
  }
  const artifact = artifactFor(interfaceConfig.artifact);
  if (!artifact) throw new Error(`Unknown tool artifact ${interfaceConfig.artifact}`);
  const [result, colorResult] = await readStepArtifactWithColors(artifact);
  activeTool = resultToObject(result, {
    fallbackColor: 0xd97706,
    colorResult,
  });
  activeTool.name = tool.label;
  activeTool.userData.interfaceId = interfaceConfig.id;
  toggleEdges(activeTool, edgesVisible);
  activeModel.add(activeTool);
  activeModel.updateMatrixWorld(true);
  clearMeasurement();
}

function renderAssemblyControls() {
  elements.visibilityControls.replaceChildren();
  elements.serviceInterfaceControls.replaceChildren();

  for (const groupConfig of activeAssemblyConfig.groups) {
    const label = document.createElement("label");
    label.className = "option-label";
    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = groupConfig.visible;
    input.addEventListener("change", () => {
      const group = semanticGroups.get(groupConfig.id);
      if (group) group.visible = input.checked;
      clearMeasurement();
    });
    label.append(input, document.createTextNode(groupConfig.label));
    elements.visibilityControls.append(label);
  }

  const tool = activeAssemblyConfig.tool;
  elements.serviceToolControls.hidden = !tool;
  if (tool) {
    elements.serviceToolLabel.textContent = `${tool.label} placement`;
    elements.serviceToolVisible.checked = tool.visible;
    activeToolInterface = tool.interfaces[0]?.id;
    for (const [index, interfaceConfig] of tool.interfaces.entries()) {
      const label = document.createElement("label");
      label.className = "option-label";
      const input = document.createElement("input");
      input.type = "radio";
      input.name = "service-interface";
      input.value = interfaceConfig.id;
      input.checked = index === 0;
      input.addEventListener("change", async () => {
        if (!input.checked) return;
        activeToolInterface = interfaceConfig.id;
        elements.serviceToolVisible.checked = true;
        try {
          await showServiceTool();
        } catch (error) {
          console.error(error);
        }
      });
      label.append(input, document.createTextNode(interfaceConfig.label));
      elements.serviceInterfaceControls.append(label);
    }
  }
  elements.assemblyControls.hidden = false;
}

async function loadStep(artifact) {
  setPreviewMode("model", "Loading STEP geometry…");
  disposeModel();
  try {
    const assemblyConfig = activeProject.viewer?.assemblies?.[artifact.path];
    const root = new THREE.Group();
    if (assemblyConfig) {
      const results = await Promise.all(
        assemblyConfig.groups.map(async (groupConfig) => {
          const groupArtifact = artifactFor(groupConfig.artifact);
          if (!groupArtifact) {
            throw new Error(`Unknown assembly artifact ${groupConfig.artifact}`);
          }
          const [result, colorResult] =
            await readStepArtifactWithColors(groupArtifact);
          return [groupConfig, result, colorResult];
        }),
      );
      for (const [groupConfig, result, colorResult] of results) {
        const group = resultToObject(result, { colorResult });
        group.name = groupConfig.label;
        group.visible = groupConfig.visible;
        group.userData.semanticGroup = groupConfig.id;
        semanticGroups.set(groupConfig.id, group);
        root.add(group);
      }
      activeAssemblyConfig = assemblyConfig;
    } else {
      const [result, colorResult] = await readStepArtifactWithColors(artifact);
      root.add(resultToObject(result, { colorResult }));
    }
    activeModel = normalizeModel(root);
    toggleEdges(activeModel, edgesVisible);
    scene.add(activeModel);
    if (activeAssemblyConfig) renderAssemblyControls();
    setPreviewMode("model");
    fitCamera();
  } catch (error) {
    console.error(error);
    setPreviewMode("model", `Unable to load STEP: ${error.message}`);
  }
}

async function loadStl(artifact) {
  setPreviewMode("model", "Loading STL geometry…");
  disposeModel();
  try {
    const response = await fetch(`./generated/${artifact.url}`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const geometry = new STLLoader().parse(await response.arrayBuffer());
    activeModel = normalizeModel(stlToObject(geometry));
    toggleEdges(activeModel, edgesVisible);
    scene.add(activeModel);
    setPreviewMode("model");
    fitCamera();
  } catch (error) {
    console.error(error);
    setPreviewMode("model", `Unable to load STL: ${error.message}`);
  }
}

async function loadText(artifact) {
  setPreviewMode("text", "Loading source…");
  const response = await fetch(`./generated/${artifact.url}`);
  elements.text.textContent = await response.text();
  setPreviewMode("text");
}

async function loadMarkdown(artifact) {
  setPreviewMode("markdown", "Rendering Markdown…");
  const response = await fetch(`./generated/${artifact.url}`);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const source = await response.text();
  elements.text.textContent = source;
  elements.markdown.innerHTML = marked.parse(source, { gfm: true });

  const documentUrl = new URL(`./generated/${artifact.url}`, window.location.href);
  for (const element of elements.markdown.querySelectorAll("[href], [src]")) {
    const attribute = element.hasAttribute("href") ? "href" : "src";
    const value = element.getAttribute(attribute);
    if (!value || value.startsWith("#")) continue;
    const resolved = new URL(value, documentUrl);
    element.setAttribute(attribute, resolved.href);
    if (attribute === "href" && resolved.origin !== window.location.origin) {
      element.setAttribute("target", "_blank");
      element.setAttribute("rel", "noreferrer");
    }
  }

  setPreviewMode("markdown");
  showMarkdownSource(false);
}

function dependencyFile(path) {
  return activeProject.dependencies.files.find((file) => file.path === path);
}

function renderTree(container, rootPath, relationship) {
  container.replaceChildren();
  const root = dependencyFile(rootPath);
  const related = root?.[relationship] || [];
  if (!related.length) {
    const empty = document.createElement("span");
    empty.className = "tree-empty";
    empty.textContent = relationship === "requires" ? "No declared inputs" : "No dependants";
    container.append(empty);
    return;
  }

  function branch(paths, ancestry) {
    const list = document.createElement("ul");
    for (const path of paths) {
      const item = document.createElement("li");
      const button = document.createElement("button");
      button.type = "button";
      button.className = "tree-node";
      button.textContent = shortPath(path);
      button.title = path;
      button.addEventListener("click", () => selectFile(path));
      item.append(button);
      if (!ancestry.has(path)) {
        const file = dependencyFile(path);
        const children = file?.[relationship] || [];
        if (children.length) {
          item.append(branch(children, new Set([...ancestry, path])));
        }
      }
      list.append(item);
    }
    return list;
  }
  container.append(branch(related, new Set([rootPath])));
}

function renderDependencies(path) {
  const file = dependencyFile(path);
  elements.selectedDependency.replaceChildren();
  if (!file) return;
  const title = document.createElement("strong");
  title.textContent = path;
  const metadata = document.createElement("span");
  metadata.textContent = `${file.kind} · ${file.sha256.slice(0, 10)}`;
  elements.selectedDependency.append(title, metadata);
  renderTree(elements.requiresTree, path, "requires");
  renderTree(elements.requiredByTree, path, "required_by");
}

function artifactFor(path) {
  return activeProject.artifacts.find((artifact) => artifact.path === path);
}

async function selectFile(path) {
  activePath = path;
  const artifact = artifactFor(path);
  document.querySelectorAll(".artifact-row").forEach((row) => {
    row.classList.toggle("active", row.dataset.path === path);
  });
  elements.viewerFile.textContent = path;
  renderDependencies(path);

  if (!artifact) {
    disposeModel();
    elements.download.hidden = true;
    setPreviewMode("model", "This dependency is metadata-only and is not published.");
    return;
  }
  elements.download.hidden = false;
  elements.download.href = `./generated/${artifact.url}`;
  elements.download.download = artifact.url.split("/").at(-1);
  const extension = path.split(".").at(-1).toLowerCase();
  if (["step", "stp"].includes(extension)) {
    await loadStep(artifact);
  } else if (extension === "stl") {
    await loadStl(artifact);
  } else if (extension === "pdf") {
    disposeModel();
    elements.pdf.src = `./generated/${artifact.url}`;
    setPreviewMode("pdf");
  } else if (["svg", "png", "jpg", "jpeg", "webp", "gif"].includes(extension)) {
    disposeModel();
    elements.drawing.src = `./generated/${artifact.url}`;
    setPreviewMode("drawing");
  } else if (extension === "md") {
    disposeModel();
    await loadMarkdown(artifact);
  } else if (["py", "json", "toml", "txt"].includes(extension)) {
    disposeModel();
    await loadText(artifact);
  } else {
    disposeModel();
    setPreviewMode("model", "Preview unavailable. Download the artifact to inspect it.");
  }
}

function renderArtifacts() {
  elements.artifactList.replaceChildren();
  const groups = Map.groupBy(
    activeProject.artifacts.filter((artifact) => !artifact.auxiliary),
    (artifact) => artifact.kind,
  );
  for (const [kind, artifacts] of groups) {
    const group = document.createElement("div");
    group.className = "artifact-group";
    const heading = document.createElement("h3");
    heading.textContent = kind.replaceAll("-", " ");
    group.append(heading);
    for (const artifact of artifacts) {
      const row = document.createElement("button");
      row.type = "button";
      row.className = "artifact-row";
      row.dataset.path = artifact.path;
      const copy = document.createElement("span");
      const name = document.createElement("strong");
      name.textContent = artifact.path.split("/").at(-1);
      const path = document.createElement("small");
      path.textContent = artifact.path;
      copy.append(name, path);
      const size = document.createElement("span");
      size.className = "artifact-size";
      size.textContent = formatBytes(artifact.size);
      row.append(copy, size);
      row.addEventListener("click", () => selectFile(artifact.path));
      group.append(row);
    }
    elements.artifactList.append(group);
  }
}

function formatPrice(price) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 6 }).format(price);
}

function bomTreeBranch(lines) {
  const list = document.createElement("ul");
  for (const line of lines) {
    const item = document.createElement("li");
    const label = document.createElement("span");
    label.className = `bom-tree-line ${line.type}`;
    const quantity = document.createElement("strong");
    quantity.textContent = `${line.quantity}×`;
    const name = document.createElement("span");
    name.textContent = line.name;
    label.append(quantity, name);
    item.append(label);
    if (line.type === "part" && line.bom?.length) {
      item.append(bomTreeBranch(line.bom));
    }
    list.append(item);
  }
  return list;
}

function renderBom() {
  const bom = activeProject.bom;
  const components = bom?.components || [];
  elements.bomPanel.hidden = !bom;
  elements.bomRows.replaceChildren();
  elements.bomTree.replaceChildren();
  if (!bom) return;

  elements.bomLineCount.textContent = components.length;
  for (const component of components) {
    const row = document.createElement("tr");
    const quantity = document.createElement("td");
    quantity.className = "bom-quantity";
    quantity.textContent = component.quantity;

    const identity = document.createElement("td");
    const name = document.createElement("strong");
    name.textContent = component.name;
    const id = document.createElement("code");
    id.textContent = component.id;
    identity.append(name, id);

    const sources = document.createElement("td");
    if (!component.sources.length) {
      const empty = document.createElement("span");
      empty.className = "bom-empty";
      empty.textContent = "No sources recorded";
      sources.append(empty);
    } else {
      const list = document.createElement("ul");
      list.className = "bom-sources";
      for (const source of component.sources) {
        const item = document.createElement("li");
        const description = document.createElement("span");
        description.textContent = source.source;
        const price = document.createElement("code");
        price.textContent = formatPrice(source.price);
        item.append(description, price);
        list.append(item);
      }
      sources.append(list);
    }
    row.append(quantity, identity, sources);
    elements.bomRows.append(row);
  }
  elements.bomTree.append(bomTreeBranch(bom.bom));
}

function chooseProject(project) {
  activeProject = project;
  elements.projectId.textContent = project.id;
  elements.projectTitle.textContent = project.name;
  elements.projectDescription.textContent = project.description;
  elements.fileCount.textContent = project.artifacts.filter(
    (artifact) => !artifact.auxiliary,
  ).length;
  document.querySelectorAll(".project-button").forEach((button) => {
    button.classList.toggle("active", button.dataset.project === project.id);
  });
  renderArtifacts();
  renderBom();
  const preferred =
    project.artifacts.find(
      (artifact) => artifact.path === project.viewer?.default,
    ) ||
    project.artifacts.find((artifact) => artifact.path.endsWith("_case.step")) ||
    project.artifacts.find((artifact) => artifact.viewable);
  if (preferred) selectFile(preferred.path);
}

function renderProjects() {
  elements.projectList.replaceChildren();
  for (const project of catalog.projects) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "project-button";
    button.dataset.project = project.id;
    const name = document.createElement("strong");
    name.textContent = project.name;
    const count = document.createElement("span");
    count.textContent = `${project.artifacts.filter((artifact) => !artifact.auxiliary).length} files`;
    button.append(name, count);
    button.addEventListener("click", () => chooseProject(project));
    elements.projectList.append(button);
  }
}

elements.wireframe.addEventListener("click", () => {
  edgesVisible = !edgesVisible;
  toggleEdges(activeModel, edgesVisible);
  elements.wireframe.classList.toggle("active", edgesVisible);
});
elements.fit.addEventListener("click", fitCamera);
elements.fullscreen.disabled = !document.fullscreenEnabled;
elements.fullscreen.addEventListener("click", async () => {
  if (document.fullscreenElement === elements.viewerCard) {
    await document.exitFullscreen();
  } else {
    await elements.viewerCard.requestFullscreen();
  }
});
document.addEventListener("fullscreenchange", () => {
  const fullscreen = document.fullscreenElement === elements.viewerCard;
  elements.fullscreen.textContent = fullscreen ? "Exit fullscreen" : "Fullscreen";
  elements.fullscreen.classList.toggle("active", fullscreen);
  elements.fullscreen.setAttribute("aria-pressed", String(fullscreen));
});
elements.serviceToolVisible.addEventListener("change", async () => {
  try {
    await showServiceTool();
  } catch (error) {
    console.error(error);
  }
});
elements.measure.addEventListener("click", () => {
  setMeasurementMode(!measurementMode);
});
elements.clearMeasurement.addEventListener("click", clearMeasurement);
elements.sourceToggle.addEventListener("click", () => {
  showMarkdownSource(!markdownSourceVisible);
});
elements.canvas.addEventListener("contextmenu", (event) => event.preventDefault());
elements.canvas.addEventListener("pointerdown", (event) => {
  if (measurementMode && event.button === 0) {
    measurementPointerStart = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
    return;
  }
  elements.canvas.classList.add("interacting");
});
elements.canvas.addEventListener("pointerup", (event) => {
  if (!measurementPointerStart || measurementPointerStart.id !== event.pointerId) return;
  const movement = Math.hypot(
    event.clientX - measurementPointerStart.x,
    event.clientY - measurementPointerStart.y,
  );
  measurementPointerStart = undefined;
  if (movement <= 5) pickMeasurementPoint(event);
});
window.addEventListener("pointerup", () => {
  elements.canvas.classList.remove("interacting");
});
window.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && measurementMode) setMeasurementMode(false);
});

const resizeObserver = new ResizeObserver(() => {
  const { width, height } = elements.viewer.getBoundingClientRect();
  renderer.setSize(width, height, false);
  camera.aspect = width / Math.max(height, 1);
  camera.updateProjectionMatrix();
});
resizeObserver.observe(elements.viewer);

function animate() {
  controls.update();
  renderer.render(scene, camera);
  requestAnimationFrame(animate);
}
animate();

try {
  const response = await fetch("./generated/catalog.json");
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  catalog = await response.json();
  elements.buildRef.textContent = `${catalog.branch} · ${catalog.commit.slice(0, 8)}`;
  renderProjects();
  if (catalog.projects.length) chooseProject(catalog.projects[0]);
} catch (error) {
  console.error(error);
  elements.projectTitle.textContent = "Catalog unavailable";
  elements.projectDescription.textContent = "Run `just site` to build project artifacts.";
  setPreviewMode("model", error.message);
}
